/**
 * What the browser tab is called, per address.
 *
 * `index.html` sets `<title>Petrovic Kundportal</title>` and nothing ever changed it, so all eight
 * client destinations, the four admin views and every page a mailed link lands on shared one name.
 * A history list of nine identical entries is not a history, and a person with the portal open in
 * two tabs could not tell them apart without switching to each one and looking.
 *
 * The suffix is always the portal, so a tab that is cropped to its first characters still says which
 * page it is. The separator is a middle dot rather than a dash, which is the same rule the rest of
 * this project's prose follows.
 *
 * The table is ordered and matched in order, because `/requests` and `/requests/142` are two
 * different names and the specific one has to be asked first. An address with no entry gets the
 * portal's own name and nothing invented: the not-found page is a real destination and naming it
 * after a path a person mistyped would put their typing error in their history.
 */

const SUFFIX = "Petrovic Systems Kundportal";

const NAMES: Array<[RegExp, (match: RegExpMatchArray) => string]> = [
  [/^\/requests\/([^/]+)\/?$/, (match) => `Ärende ${decodeURIComponent(match[1])}`],
  [/^\/requests\/?$/, () => "Ärenden"],
  [/^\/overview\/?$/, () => "Översikt"],
  [/^\/chat\/?$/, () => "Chatt"],
  [/^\/accounts\/?$/, () => "Konton"],
  [/^\/documents\/?$/, () => "Dokument"],
  [/^\/settings(\/[^/]*)?\/?$/, () => "Inställningar"],
  [/^\/notifications\/?$/, () => "Notiser"],
  /*
    Specific before general, the same ordering `/requests/:number` follows two entries above. The id
    is deliberately not in the title: it is the transfer's UUID and the tab strip is not a place to
    publish one, unlike a request number, which is the tenant's own and is what a client says out
    loud on the phone.
  */
  [/^\/secrets\/[^/]+\/?$/, () => "Hemlighet"],
  [/^\/secrets\/?$/, () => "Hemligheter"],
  // Build round 19, 2026-10-08: the list of websites and one website alike. The second segment is
  // the service's UUID, which the tab strip is no place for, as with a transfer's id above.
  [/^\/site-facts(\/[^/]*)?\/?$/, () => "Webbplatsens fakta"],
  // Build round 37, 2026-10-11.
  [/^\/statistics\/?$/, () => "Statistik"],
  [/^\/login\/?$/, () => "Logga in"],
  [/^\/verify\/?$/, () => "Verifiering"],
  [/^\/invite\/?$/, () => "Välj lösenord"],
  [/^\/new-password\/?$/, () => "Nytt lösenord"],
  // Round C, 2026-09-22: the link mailed to a new sign-in address.
  [/^\/confirm-email\/?$/, () => "Bekräfta ny adress"],
  [/^\/recover\/?$/, () => "Återställ lösenord"],
  // 2026-10-06: the privacy information, open to anybody.
  [/^\/privacy\/?$/, () => "Dina uppgifter"],
  // Petrovic's own four. They carry the same suffix as a client's pages, because the suffix names
  // the product and not the audience, and the leading word is what tells the two apart in a tab
  // strip that holds both at once.
  [/^\/admin\/requests(\/.*)?$/, () => "Ärenden, Petrovic Systems"],
  // Every tab of Drift and Underlag, and one client's page, since the tabs became addresses on
  // 2026-09-24. The client's name is not in the title: the tab strip is not where a name belongs.
  [/^\/admin\/operations(\/[^/]*)?\/?$/, () => "Drift, Petrovic Systems"],
  [/^\/admin\/clients\/[^/]+(\/[^/]*)?\/?$/, () => "Kund, Petrovic Systems"],
  [/^\/admin\/clients\/?$/, () => "Kunder"],
  [/^\/admin\/records(\/[^/]*)?\/?$/, () => "Underlag"],
  [/^\/admin\/inbox\/?$/, () => "Inkorg, Petrovic Systems"],
  [/^\/admin\/settings\/?$/, () => "Inställningar, Petrovic Systems"],
  [/^\/admin\/notifications\/?$/, () => "Notiser, Petrovic Systems"]
];

export function titleForPath(pathname: string): string {
  for (const [pattern, name] of NAMES) {
    const match = pathname.match(pattern);
    if (match) {
      return `${name(match)} · ${SUFFIX}`;
    }
  }

  return SUFFIX;
}
